import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiEdit, FiArrowLeft, FiRefreshCw, FiUsers, FiUser, FiPhone, FiMail, FiMessageSquare, FiCheckCircle, FiMapPin, FiCalendar, FiBookOpen, FiGlobe, FiFileText, FiCopy } from 'react-icons/fi';
import { prospectsAPI, settingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { toast } from 'react-toastify';
import Modal from '../../components/Common/Modal';

const REMARKS_LABEL_MAP = {
  form_source: 'Form Source',
  legacy_lead_ref: 'Website Lead Ref',
  legacy_source: 'Original Source',
  message: 'Message',
  form_id: 'Form ID',
  country_of_origin: 'Country of Origin',
  favored_diploma: 'Favored Diploma',
  favored_intake: 'Favored Intake',
  prenom: 'Prénom',
  formation_souhaitee: 'Formation Souhaitée',
  niveau_actuel: 'Niveau Actuel',
  event_date: 'Event Date',
  campus_to_contact: 'Campus to Contact',
  consent: 'Consent',
  form_type: 'Form Type',
  form_title: 'Form Title',
  current_url: 'Source URL',
};

function formatRemarksKey(key) {
  if (REMARKS_LABEL_MAP[key]) return REMARKS_LABEL_MAP[key];
  return String(key).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function RemarksDisplay({ value }) {
  let parsed = null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try { parsed = JSON.parse(trimmed); } catch (_) { /* not JSON */ }
    }
  } else if (typeof value === 'object' && value !== null) {
    parsed = value;
  }

  if (!parsed || typeof parsed !== 'object') {
    return <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{value}</p>;
  }

  const { raw_payload, ...mainFields } = (Array.isArray(parsed) ? { items: parsed } : parsed);

  const entries = Object.entries(mainFields).filter(([, v]) => v !== null && v !== undefined && v !== '');

  return (
    <div style={{ marginTop: 8 }}>
      {entries.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, auto) 1fr', gap: '6px 16px', fontSize: 14 }}>
          {entries.map(([key, val]) => (
            <React.Fragment key={key}>
              <span style={{ color: 'var(--text-muted)', fontWeight: 500, whiteSpace: 'nowrap' }}>{formatRemarksKey(key)}</span>
              <span style={{ color: 'var(--text)', wordBreak: 'break-word' }}>
                {typeof val === 'object' ? JSON.stringify(val) : String(val)}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
      {raw_payload && typeof raw_payload === 'object' && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', userSelect: 'none' }}>
            Raw Form Data
          </summary>
          <div style={{
            marginTop: 6, padding: '10px 12px', background: 'var(--bg-secondary, #f5f5f5)',
            borderRadius: 6, fontSize: 13, maxHeight: 260, overflowY: 'auto',
            display: 'grid', gridTemplateColumns: 'minmax(120px, auto) 1fr', gap: '4px 12px'
          }}>
            {Object.entries(raw_payload)
              .filter(([, v]) => v !== null && v !== undefined && v !== '')
              .map(([k, v]) => (
                <React.Fragment key={k}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 500, whiteSpace: 'nowrap', fontSize: 12 }}>
                    {formatRemarksKey(k)}
                  </span>
                  <span style={{ color: 'var(--text)', wordBreak: 'break-word', fontSize: 12 }}>
                    {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                  </span>
                </React.Fragment>
              ))}
          </div>
        </details>
      )}
    </div>
  );
}

const ProspectDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { canPerformAction } = useAuth();
  const { getConfig } = useCountry();
  const config = getConfig();
  const showInterestedCourse = !!config.features?.interestedCourse;

  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [activities, setActivities] = useState([]);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityForm, setActivityForm] = useState({
    activity_type: 'Call',
    activity_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
    activity_time: '',
    next_followup: '',
    notes: ''
  });
  const copyToClipboard = useCallback(async (text, kind) => {
    const value = String(text || '').trim();
    if (!value || value === '-') return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(kind === 'phone' ? 'Phone number copied' : 'Email copied');
    } catch (_) {
      toast.error(t('students.copyFailed', { defaultValue: 'Copy failed' }));
    }
  }, [t]);

  const loadActivities = useCallback(async () => {
    try {
      const activitiesRes = await prospectsAPI.getActivities(id);
      setActivities(activitiesRes.data || []);
    } catch {
      setActivities([]);
    }
  }, [id]);

  const loadProspect = useCallback(async () => {
    setLoading(true);
    try {
      const [prospectRes, statusesRes, activitiesRes] = await Promise.allSettled([
        prospectsAPI.getById(id),
        settingsAPI.getProspectStatuses(),
        prospectsAPI.getActivities(id)
      ]);

      if (prospectRes.status === 'fulfilled') {
        setProspect(prospectRes.value?.data || null);
      } else {
        throw prospectRes.reason;
      }
      setStatuses(statusesRes.status === 'fulfilled' ? (statusesRes.value?.data || []) : []);
      setActivities(activitiesRes.status === 'fulfilled' ? (activitiesRes.value?.data || []) : []);
    } catch {
      toast.error(t('prospects.loadFailed'));
    }
    setLoading(false);
  }, [id, t]);

  useEffect(() => { loadProspect(); }, [loadProspect]);

  const statusOptions = useMemo(() => {
    if (!prospect) return [];
    const active = (statuses || [])
      .filter(s => s.status === 'active' && s.prospect_group === prospect.prospect_type)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map(s => s.status_name);
    if (prospect.status && !active.includes(prospect.status)) active.unshift(prospect.status);
    return active;
  }, [statuses, prospect]);

  const handleStatusChange = async (newStatus) => {
    if (!prospect || newStatus === prospect.status) return;
    setUpdatingStatus(true);
    try {
      await prospectsAPI.update(id, { status: newStatus });
      setProspect(prev => ({ ...prev, status: newStatus }));
      toast.success(t('prospects.statusUpdated'));
    } catch {
      toast.error(t('prospects.statusUpdateFailed'));
    }
    setUpdatingStatus(false);
  };

  const handleConvert = () => {
    if (!prospect) return;
    if (prospect.prospect_type === 'B2B') {
      navigate(`/b2b-agencies/new?fromProspect=${id}`);
    } else {
      navigate(`/students/new?fromProspect=${id}`);
    }
  };

  const handleLogActivity = async () => {
    if (!activityForm.activity_type || !activityForm.activity_date || !String(activityForm.notes || '').trim()) {
      toast.error(t('prospects.activityRequired'));
      return;
    }
    try {
      setActivitySaving(true);
      await prospectsAPI.createActivity(id, activityForm);
      toast.success(t('prospects.activityLogged'));
      setShowActivityModal(false);
      setActivityForm({
        activity_type: 'Call',
        activity_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
        activity_time: '',
        next_followup: '',
        notes: ''
      });
      loadActivities();
    } catch (err) {
      toast.error(err.response?.data?.message || t('prospects.activityLogFailed'));
    } finally {
      setActivitySaving(false);
    }
  };

  const statusBadgeColor = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'new') return { bg: '#EEF2FF', color: '#4F46E5' };
    if (s === 'contacted') return { bg: '#FEF3C7', color: '#92400E' };
    if (s === 'interested') return { bg: '#ECFDF5', color: '#059669' };
    if (s === 'not interested') return { bg: '#FEF2F2', color: '#DC2626' };
    if (s === 'converted') return { bg: '#D1FAE5', color: '#065F46' };
    return { bg: '#F1F5F9', color: '#475569' };
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!prospect) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <p>{t('prospects.notFound')}</p>
        <button className="btn btn-primary" onClick={() => navigate('/prospects')}>{t('prospects.backToList')}</button>
      </div>
    );
  }

  const badge = statusBadgeColor(prospect.status);
  const isConverted = !!prospect.converted;
  const isB2B = prospect.prospect_type === 'B2B';

  const locationParts = [prospect.city, prospect.state, prospect.country].filter(Boolean);
  const locationStr = locationParts.length > 0 ? locationParts.join(', ') : '-';

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/prospects')} title={t('common.back')}>
            <FiArrowLeft size={16} />
          </button>
          <div>
            <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
              {prospect.name}
              <span style={{
                padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                background: isB2B ? '#EEF2FF' : '#FEF3C7',
                color: isB2B ? '#4F46E5' : '#92400E'
              }}>
                {prospect.prospect_type}
              </span>
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{prospect.prospect_ref}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={loadProspect}><FiRefreshCw size={14} /></button>
          {canPerformAction('prospects', 'update') && (
            <button className="btn btn-outline" onClick={() => navigate(`/prospects/${id}/edit`)}>
              <FiEdit size={14} /> {t('common.edit')}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
        {/* Main info */}
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16 }}>{t('prospects.details')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <InfoRow icon={<FiUser />} label={t('prospects.name')} value={prospect.name} />
            <InfoRow icon={isB2B ? <FiUsers /> : <FiUser />}
              label={t('prospects.type')} value={prospect.prospect_type} />
            <InfoRow
              icon={<FiPhone />}
              label={t('prospects.contact')}
              value={prospect.contact_number ? (
                <button
                  type="button"
                  onClick={() => copyToClipboard(`${prospect.phone_country_code || ''} ${prospect.contact_number || ''}`.trim(), 'phone')}
                  style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                  title="Copy phone number"
                >
                  <span>{`${prospect.phone_country_code || ''} ${prospect.contact_number || ''}`.trim()}</span>
                  <FiCopy size={13} />
                </button>
              ) : '-'}
            />
            <InfoRow
              icon={<FiMail />}
              label={t('prospects.email')}
              value={prospect.email ? (
                <button
                  type="button"
                  onClick={() => copyToClipboard(prospect.email, 'email')}
                  style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                  title="Copy email"
                >
                  <span>{prospect.email}</span>
                  <FiCopy size={13} />
                </button>
              ) : '-'}
            />

            {/* B2B-specific fields */}
            {isB2B && (
              <>
                <InfoRow icon={<FiUser />} label={t('leadForm.contactPerson')} value={prospect.decision_maker || '-'} />
                <InfoRow icon={<FiBookOpen />} label={t('leadForm.source')} value={prospect.source || '-'} />
                <InfoRow icon={<FiMapPin />} label={t('prospects.location')} value={locationStr} />
              </>
            )}

            {/* B2C-specific fields */}
            {!isB2B && (
              <>
                {prospect.title && (
                  <InfoRow icon={<FiUser />} label={t('studentForm.titleLabel')} value={prospect.title} />
                )}
                <InfoRow icon={<FiCalendar />} label={t('studentForm.dob')}
                  value={prospect.dob ? new Date(prospect.dob).toLocaleDateString() : '-'} />
                <InfoRow icon={<FiFileText />} label={t('studentForm.passportNumber')} value={prospect.passport_no || '-'} />
                <InfoRow icon={<FiUser />} label={t('common.gender')} value={prospect.gender || '-'} />
                <InfoRow icon={<FiGlobe />} label={t('studentForm.languageLabel')} value={prospect.language || '-'} />
                <InfoRow icon={<FiBookOpen />} label={t('studentForm.lastDegree')} value={prospect.current_degree || '-'} />
                {showInterestedCourse && prospect.interested_course && (
                  <InfoRow icon={<FiBookOpen />} label={t('prospects.interestedCourse')} value={prospect.interested_course} />
                )}
                <InfoRow icon={<FiBookOpen />} label={t('studentForm.source')} value={prospect.source || '-'} />
                {prospect.address && (
                  <InfoRow icon={<FiMapPin />} label={t('common.address')} value={prospect.address} />
                )}
                <InfoRow icon={<FiMapPin />} label={t('prospects.location')} value={locationStr} />
                {prospect.zip_code && (
                  <InfoRow icon={<FiMapPin />} label={t('common.zipCode')} value={prospect.zip_code} />
                )}
              </>
            )}

            <InfoRow icon={<FiUsers />} label={t('prospects.owner')} value={prospect.owner_name || '-'} />
            <InfoRow icon={<FiUsers />} label={t('prospects.createdBy')} value={prospect.created_by_name || '-'} />
            <InfoRow icon={<FiMessageSquare />} label={t('prospects.created')}
              value={prospect.created_at ? new Date(prospect.created_at).toLocaleString() : '-'} />
          </div>
          {prospect.remarks && (
            <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('prospects.remarks')}</label>
              <RemarksDisplay value={prospect.remarks} />
            </div>
          )}
        </div>

        {/* Sidebar: Status + Convert */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8, display: 'block' }}>
              {t('prospects.statusLabel')}
            </label>
            <span style={{
              display: 'inline-block', padding: '4px 14px', borderRadius: 12, fontSize: 13, fontWeight: 600,
              background: badge.bg, color: badge.color, marginBottom: 12
            }}>
              {prospect.status}
            </span>
            {canPerformAction('prospects', 'update') && !isConverted && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, display: 'block' }}>
                  {t('prospects.changeStatus')}
                </label>
                <select
                  value={prospect.status}
                  onChange={e => handleStatusChange(e.target.value)}
                  disabled={updatingStatus}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13 }}
                >
                  {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12, display: 'block' }}>
              {t('prospects.conversion')}
            </label>
            {isConverted ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <FiCheckCircle size={16} style={{ color: 'var(--success)' }} />
                  <span style={{ fontWeight: 600, color: 'var(--success)', fontSize: 13 }}>{t('prospects.alreadyConverted')}</span>
                </div>
                <Link
                  to={prospect.converted_entity_type === 'agency'
                    ? `/b2b-agencies/${prospect.converted_entity_id}`
                    : `/students/${prospect.converted_entity_id}`}
                  className="btn btn-outline btn-sm"
                  style={{ width: '100%', textAlign: 'center', display: 'block' }}
                >
                  {prospect.converted_entity_type === 'agency' ? t('prospects.viewAgency') : t('prospects.viewStudent')}
                </Link>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px' }}>
                  {isB2B ? t('prospects.convertB2BHint') : t('prospects.convertB2CHint')}
                </p>
                {canPerformAction('prospects', 'update') && (
                  <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleConvert}>
                    {isB2B ? t('prospects.convertToAgency') : t('prospects.convertToStudent')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>{t('prospects.activitiesTitle')}</h3>
          {canPerformAction('prospects', 'update') && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowActivityModal(true)}>
              {t('prospects.logActivity')}
            </button>
          )}
        </div>
        <div className="timeline">
          {activities.map((a) => (
            <div className="timeline-item" key={a.prospect_activity_id}>
              <div className="timeline-header">
                <div className="timeline-type">
                  <span className="badge badge-new">{a.activity_type}</span>
                </div>
                <div className="timeline-date">
                  {new Date(a.activity_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} {a.activity_time || ''}
                </div>
              </div>
              <div className="timeline-notes">{a.notes}</div>
              <div className="timeline-meta">
                {a.next_followup && (
                  <span>{t('prospects.nextFollowupLabel')} {new Date(a.next_followup).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
                )}
                <span>{t('prospects.byLabel')} {a.created_by_name || '-'}</span>
              </div>
            </div>
          ))}
          {activities.length === 0 && <div className="empty-state"><p>{t('prospects.noActivities')}</p></div>}
        </div>
      </div>

      <Modal
        isOpen={showActivityModal}
        onClose={() => { if (!activitySaving) setShowActivityModal(false); }}
        title={t('prospects.activityModal')}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setShowActivityModal(false)} disabled={activitySaving}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" onClick={handleLogActivity} disabled={activitySaving}>
              {activitySaving ? t('common.saving') : t('prospects.saveActivity')}
            </button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('prospects.activityTypeLabel')} <span className="required">*</span></label>
            <select value={activityForm.activity_type} onChange={(e) => setActivityForm((f) => ({ ...f, activity_type: e.target.value }))}>
              {['Call', 'Email', 'WhatsApp', 'Meeting', 'Follow-up'].map((at) => <option key={at} value={at}>{at}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('prospects.activityDateLabel')} <span className="required">*</span></label>
            <input type="date" value={activityForm.activity_date} onChange={(e) => setActivityForm((f) => ({ ...f, activity_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('prospects.activityTimeLabel')}</label>
            <input type="time" value={activityForm.activity_time} onChange={(e) => setActivityForm((f) => ({ ...f, activity_time: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('prospects.nextFollowupDate')}</label>
            <input type="date" value={activityForm.next_followup} onChange={(e) => setActivityForm((f) => ({ ...f, next_followup: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('prospects.activityNotesLabel')} <span className="required">*</span></label>
            <textarea value={activityForm.notes} onChange={(e) => setActivityForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
    <div style={{ color: 'var(--text-light)', marginTop: 2 }}>{icon}</div>
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--text)', marginTop: 2 }}>{value}</div>
    </div>
  </div>
);

export default ProspectDetail;
